import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAudio } from '../AudioContext'
import { useStreamingLinks } from '../useStreamingLinks'
import StreamingModal from '../components/StreamingModal'
import SEO from '../components/SEO'
import './Home.css'

const API_URL = 'https://radio.stellarfm.fr/api/nowplaying/stellarfm'
const FILES_URL = 'https://radio.stellarfm.fr/api/station/1/files'
const API_KEY = 'd4e4849306909a66:369a0433018743530e8be04a903c285d'

const DiscordIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
  </svg>
)

const PlayIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M8 5v14l11-7z" />
  </svg>
)

const PauseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
  </svg>
)

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M7 17l9.2-9.2M17 17V7H7" />
  </svg>
)

export default function Home() {
  const { isPlaying, toggle } = useAudio()
  const streaming = useStreamingLinks()
  const [nowPlaying, setNowPlaying] = useState(null)
  const [listeners, setListeners] = useState(0)
  const [hits, setHits] = useState([])
  const [newsItems, setNewsItems] = useState([])
  const [openArticle, setOpenArticle] = useState(null)

  // Now Playing
  useEffect(() => {
    const load = () =>
      fetch(API_URL)
        .then(r => r.json())
        .then(d => {
          setNowPlaying(d?.now_playing?.song || null)
          setListeners(d?.listeners?.total ?? 0)
        })
        .catch(() => { })
    load()
    const iv = setInterval(load, 15000)
    return () => clearInterval(iv)
  }, [])

  // Hits from station files API
  useEffect(() => {
    fetch(FILES_URL, {
      headers: { 'Accept': 'application/json', 'X-API-Key': API_KEY }
    })
      .then(r => r.json())
      .then(files => {
        const heavy = files.filter(f =>
          f.playlists?.some(p => typeof p === 'object' && p.name && p.name.startsWith('[HEAVY]'))
        )
        // Shuffle and pick 12
        const shuffled = heavy.sort(() => Math.random() - 0.5).slice(0, 12)
        setHits(shuffled)
      })
      .catch(() => { })
  }, [])

  // News
  useEffect(() => {
    fetch('/news.json')
      .then(r => r.json())
      .then(setNewsItems)
      .catch(() => { })
  }, [])

  // Modal escape
  useEffect(() => {
    if (!openArticle) return
    const onKey = (e) => { if (e.key === 'Escape') setOpenArticle(null) }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [openArticle])

  return (
    <div className="home page">
      <SEO
        title="StellarFM — Le son de maintenant."
        description="Webradio française indépendante en direct 24h/24. Écoute gratuite en ligne des meilleurs hits du moment, émissions, dédicaces et actualités."
      />

      {/* ── Hero ── */}
      <section className="home__hero">
        <div className="home__hero-inner">
          <p className="home__eyebrow">Webradio française indépendante</p>
          <h1 className="home__title">
            Stellar<span>FM</span>
          </h1>
          <p className="home__subtitle">Le son de maintenant</p>

          <div className="home__ctas">
            <button className="home__play-btn" onClick={toggle}>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
              {isPlaying && nowPlaying
                ? <span className="home__play-info">
                  <span className="home__play-title">{nowPlaying.title}</span>
                  <span className="home__play-artist">{nowPlaying.artist}</span>
                </span>
                : isPlaying ? 'En écoute...' : 'Écouter en direct'
              }
            </button>
            <a
              href="https://discord.gg/yMkmQbM9P6"
              target="_blank"
              rel="noopener noreferrer"
              className="home__discord-btn"
            >
              <DiscordIcon />
              Discord
            </a>
          </div>

          {listeners > 0 && (
            <p className="home__listeners">
              <span className="home__listeners-dot" />
              {listeners} auditeur{listeners !== 1 ? 's' : ''} en ce moment
            </p>
          )}
        </div>
      </section>

      {/* ── Now Playing ── */}
      {nowPlaying && (
        <section className="home__now">
          <div className="container">
            <Link to="/radio" className="home__now-card">
              {nowPlaying.art && (
                <img src={nowPlaying.art} alt="" className="home__now-art" loading="lazy" decoding="async" />
              )}
              <div className="home__now-info">
                <span className="home__now-label">À l'antenne</span>
                <span className="home__now-title">{nowPlaying.title}</span>
                <span className="home__now-artist">{nowPlaying.artist}</span>
              </div>
              <span className="home__now-link">
                Voir le player <ArrowIcon />
              </span>
            </Link>
          </div>
        </section>
      )}

      {/* ── Hits du moment ── */}
      {hits.length > 0 && (
        <section className="home__hits">
          <div className="container">
            <h2 className="home__section-label">Hits du moment</h2>
            <div className="home__hits-grid">
              {hits.map(track => (
                <div key={track.id} className="home__hit" onClick={() => streaming.open(track)}>
                  <img
                    src={track.art}
                    alt={track.title}
                    className="home__hit-art"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="home__hit-info">
                    <span className="home__hit-title">{track.title}</span>
                    <span className="home__hit-artist">{track.artist}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Features ── */}
      <section className="home__features">
        <div className="container">
          <div className="home__features-grid">
            <Link to="/radio" className="home__feature">
              <span className="home__feature-icon">🎧</span>
              <h3>Radio 24/7</h3>
              <p>Musique non-stop, hits du moment et classiques.</p>
            </Link>
            <Link to="/dedicaces" className="home__feature">
              <span className="home__feature-icon">💬</span>
              <h3>Dédicaces</h3>
              <p>Envoie un message à l'antenne en direct.</p>
            </Link>
            <Link to="/integrations" className="home__feature">
              <span className="home__feature-icon">🎮</span>
              <h3>Intégrations</h3>
              <p>Écoute StellarFM dans tes jeux et apps.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Discord ── */}
      <section className="home__discord">
        <div className="container">
          <a
            href="https://discord.gg/yMkmQbM9P6"
            target="_blank"
            rel="noopener noreferrer"
            className="home__discord-bar"
          >
            <DiscordIcon />
            <span>Rejoins la communauté sur <strong>Discord</strong></span>
            <span className="home__discord-arrow">→</span>
          </a>
        </div>
      </section>

      {/* ── Actualités ── */}
      {newsItems.length > 0 && (
        <section className="home__news">
          <div className="container">
            <h2 className="home__section-label">Actualités</h2>
            <div className="home__news-grid">
              {newsItems.map(item => {
                const card = (
                  <article
                    key={item.id}
                    className="home__news-card"
                    onClick={item.body ? () => setOpenArticle(item) : undefined}
                  >
                    <div className="home__news-top">
                      <span className="home__news-tag">{item.tag}</span>
                      <span className="home__news-date">{item.date}</span>
                    </div>
                    <h3 className="home__news-title">{item.title}</h3>
                    <p className="home__news-excerpt">{item.excerpt}</p>
                    {(item.link || item.body) && (
                      <span className="home__news-read">Lire →</span>
                    )}
                  </article>
                )
                return item.link
                  ? <a key={item.id} href={item.link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{card}</a>
                  : card
              })}
            </div>
          </div>
        </section>
      )}

      <StreamingModal
        track={streaming.openTrack}
        links={streaming.links}
        loading={streaming.loading}
        platforms={streaming.PLATFORMS}
        onClose={streaming.close}
      />

      {/* ── Article modal ── */}
      {openArticle && (
        <div className="article-overlay" onClick={() => setOpenArticle(null)}>
          <div className="article-modal" onClick={e => e.stopPropagation()}>
            <button className="article-modal__close" onClick={() => setOpenArticle(null)} aria-label="Fermer">✕</button>
            <div className="article-modal__meta">
              <span className="home__news-tag">{openArticle.tag}</span>
              <span className="home__news-date">{openArticle.date}</span>
            </div>
            <h2 className="article-modal__title">{openArticle.title}</h2>
            <div className="article-modal__body">
              {openArticle.body.map((para, i) => (
                <p key={i}>{para}</p>
              ))}
            </div>
            {openArticle.cta && (
              <a
                href={openArticle.cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="article-modal__cta"
              >
                {openArticle.cta.label}
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
